import styled, { CSSObject } from '@emotion/styled'
import {
  useCartMatch,
  useConsumerCart,
  useModal,
  useQueryParams,
  useShop
} from 'shop/hooks'
import { StyledHTMLElement } from 'shop/theme/types'
import { FulfillmentTime } from '../shared/FulfillmentTime/FulfillmentTime'
import { Semibold } from '../common'
import { IoChevronForward } from 'react-icons/io5'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { getFulfillmentDetails } from './utils'
import { getFulfillmentFlags } from 'shop/utils'
import {
  canAcceptOrders,
  findDefaultFulfillment,
  isAnyStoreAcceptingOrders
} from 'shop/utils/common'
import { FulfillmentType } from 'shop/types'
import ContentLoader from 'react-content-loader'

const FulfillmentSummary = () => {
  const { openModal, isModalOpen, fulfillmentModal, closeModal } = useModal()
  const navigate = useNavigate()
  const location = useLocation()
  const { currentPartnerStore, partner, isPartnerLoading } = useShop()
  const { isCartMatch } = useCartMatch()
  const [shouldShowFulfillmentModal, setShouldShowFulfillmentModal] =
    useState(false)
  const { cart } = useConsumerCart()
  const { getQueryParam } = useQueryParams()

  const queryParamFulfillmentType =
    (getQueryParam('fulfillment_type').toUpperCase() as FulfillmentType) ||
    undefined

  const cartMinimumOrderValue = cart && (cart.minimumOrderValue || '0')

  const isAnyStoreAvailable = isAnyStoreAcceptingOrders(partner?.stores)

  const isStoreAcceptingOrders = useMemo(
    () => canAcceptOrders(currentPartnerStore, queryParamFulfillmentType),
    [currentPartnerStore, queryParamFulfillmentType]
  )

  const defaultFulfillmentInfo =
    findDefaultFulfillment(
      currentPartnerStore?.orderFulfillmentTypes,
      queryParamFulfillmentType
    ) ?? null

  const cartFulfillment = cart?.fulfillment || null

  const { isOrderAtTable } = getFulfillmentFlags(cartFulfillment?.type)

  const {
    isDelivery,
    defaultFulfillment,
    fulfillmentWindow,
    orderType,
    isOat
  } = getFulfillmentDetails(
    cartFulfillment,
    defaultFulfillmentInfo,
    isCartMatch
  )

  // Only use cart minimum order value if there's a cart match, otherwise use default fulfillment value
  const minimumOrderValue = parseFloat(
    (isCartMatch && cartMinimumOrderValue) ||
      defaultFulfillment?.minimumOrderValue ||
      '0'
  )

  const userCanChangeFulfillment = cartFulfillment
    ? !isOrderAtTable && isAnyStoreAvailable
    : isAnyStoreAvailable

  const openFulfilmentModal = () => {
    if (!isModalOpen('fulfillment')) {
      setShouldShowFulfillmentModal(true)
      openModal('fulfillment')
    }
  }

  const handleFulfillmentModalClose = () => {
    setShouldShowFulfillmentModal(false)
    closeModal('fulfillment')
  }

  useEffect(() => {
    const queryParam = new URLSearchParams(location.search)
    // If fulfillment query param from login modal flow exists, remove it
    if (queryParam.get('fulfillment') && !isModalOpen('fulfillment')) {
      setShouldShowFulfillmentModal(true)
      openModal('fulfillment')
      navigate({
        pathname: location.pathname,
        search: queryParam.toString()
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isLoadingInfo = useMemo(
    () => isPartnerLoading || !currentPartnerStore,
    [isPartnerLoading, currentPartnerStore]
  )

  if (isLoadingInfo) {
    // Loading state whilst Partner data is updated
    return (
      <Root data-testid='fulfillment-summary'>
        <Container>
          <InfoLoader />
        </Container>
      </Root>
    )
  }

  return (
    <Root data-testid='fulfillment-summary'>
      {isModalOpen('fulfillment') &&
        shouldShowFulfillmentModal &&
        fulfillmentModal({
          onClose: handleFulfillmentModalClose
        })}

      {isOat ? (
        <></>
      ) : (
        <Container
          onClick={userCanChangeFulfillment ? openFulfilmentModal : undefined}
        >
          <Info>
            <Row data-testid='fulfillment-summary-row'>
              {!isStoreAcceptingOrders ? (
                <Line>Currently not accepting orders</Line>
              ) : (
                <>
                  {fulfillmentWindow && (
                    <Line>
                      {isDelivery ? 'Deliver ' : 'Pickup '}
                      <FulfillmentTime
                        fulfillmentWindow={fulfillmentWindow}
                        orderType={orderType}
                      />

                      {minimumOrderValue > 0 && (
                        <>
                          {' • '}
                          <Line>
                            <Semibold>
                              {' '}
                              £{minimumOrderValue.toFixed(2)}
                            </Semibold>{' '}
                            minimum order
                          </Line>
                        </>
                      )}
                    </Line>
                  )}
                </>
              )}
            </Row>
          </Info>
          {userCanChangeFulfillment && (
            <ChevronContainer>
              <IoChevronForward />
            </ChevronContainer>
          )}
        </Container>
      )}
    </Root>
  )
}

const Root = styled.div<StyledHTMLElement>(({ theme }) => ({
  display: 'flex',
  position: 'absolute',
  margin: 'auto',
  right: '12px',
  bottom: '27px',
  background: 'white',
  borderRadius: '6px',
  border: '1px solid black',
  maxWidth: '90%',
  cursor: 'pointer',
  [theme.mediaQueries.viewport4]: {
    right: '27px',
    bottom: '29px'
  },
  [theme.mediaQueries.viewport7]: {
    bottom: '38px',
    right: '27px'
  },
  [theme.mediaQueries.viewport14]: {
    right: '87px'
  }
}))

const Info = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    display: 'flex',
    flexFlow: 'column'
  })
)

const Container = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    display: 'flex',
    padding: '8px 0 8px 16px'
  })
)

export const Row = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    display: 'block'
  })
)

const Line = styled.span<StyledHTMLElement>(({ theme }) => ({
  fontFamily: theme.fonts.body.family,
  fontWeight: 400,
  fontSize: '14px',
  margin: 0,
  color: '#59595A',
  lineHeight: '20px',
  height: '20px',
  b: {
    color: 'black'
  },
  marginRight: '4px',
  [theme.mediaQueries.viewport7]: {
    marginRight: '0'
  }
}))

export const Header = styled.h1<StyledHTMLElement>(({ theme }) => ({
  fontFamily: theme.fonts.heading.family,
  fontSize: theme.fontSizes[3],
  marginTop: 0,
  color: theme.colors.primary,
  marginBottom: 0,
  [theme.mediaQueries.viewport7]: {
    fontSize: theme.fontSizes[5]
  }
}))

const ChevronContainer = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    display: 'flex',
    alignItems: 'center',
    padding: '0 10px 0 12px',
    color: '#59595A'
  })
)

const InfoLoader = styled(ContentLoader)(() => ({
  width: '300px',
  height: '18px',
  borderRadius: '8px',
  marginRight: '16px'
}))

export default FulfillmentSummary
